import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Upload, X, ArrowLeft, Package } from 'lucide-react';
import type { Database } from '@/lib/database.types';
import { RichTextEditor } from '@/components/RichTextEditor';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

type Producer = Database['public']['Tables']['producers']['Row'] & {
  profiles?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    phone: string | null;
    address: string | null;
  };
};

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function ProducerEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [uploadError, setUploadError] = React.useState('');
  const [producer, setProducer] = React.useState<Producer | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    companyName: '',
    description: '',
    websiteUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: ''
  });
  const [contactData, setContactData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: ''
  });
  const [successMessage, setSuccessMessage] = React.useState('');

  // No bucket creation - it should already exist in your project
  // We'll just use the existing product_images bucket for all images

  React.useEffect(() => {
    if (!id) return;
    if (!user) {
      console.error('No active user. User must be logged in to edit producers.');
      setError(t('common.authRequired', 'Autentisering krävs. Vänligen logga in igen.'));
      return;
    }

    async function fetchProducer() {
      try {
        setLoading(true);
        setError('');

        console.log('Fetching producer with ID:', id);
        console.log('Current authenticated user:', user);

        // Fetch producer by ID
        const { data, error } = await supabase
          .from('producers')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching producer:', error);
          throw error;
        }

        if (!data) {
          console.error('No producer found with ID:', id);
          throw new Error('Producer not found');
        }

        console.log('Producer data fetched:', data);
        
        // Fetch profile data separately
        let profileData = null;
        // The producer's ID is the same as the profile ID in the database schema
        const profileId = data.id;
        
        if (profileId) {
          console.log('Fetching profile for ID:', profileId);
          // Check if the current user is an admin (admins can edit all profiles)
          const { data: currentUser, error: userError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          
          if (userError) {
            console.error('Error fetching current user role:', userError);
          } else {
            console.log('Current user role:', currentUser.role);
          }
          
          const { data: profileResult, error: profileFetchError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email, phone, address, role')
            .eq('id', profileId)
            .single();
            
          if (!profileFetchError) {
            profileData = profileResult;
            console.log('Profile data fetched:', profileData);
          } else {
            console.error('Error fetching profile:', profileFetchError);
            
            // Check if this is a "not found" error
            if (profileFetchError.code === 'PGRST116') {
              console.warn('No profile found for this producer. This may indicate a data consistency issue.');
              // Create a profile for this producer if none exists
              console.log('Attempting to find Auth user with matching ID');
              
              // Try to get the auth user with this ID to see if they exist
              const { data: authUser, error: authError } = await supabase.auth.admin.getUserById(profileId);
              
              if (!authError && authUser) {
                console.log('Found matching Auth user:', authUser);
                console.log('Will use this data to populate profile form');
              } else {
                console.warn('No matching Auth user found. This may require manual intervention.');
              }
            }
          }
        }
        
        // Combine the data
        const producerWithProfile = {
          ...data,
          profiles: profileData
        };
        
        setProducer(producerWithProfile);
        setFormData({
          companyName: data.company_name || '',
          description: data.description || '',
          websiteUrl: data.website_url || '',
          facebookUrl: data.facebook_url || '',
          instagramUrl: data.instagram_url || '',
          twitterUrl: data.twitter_url || ''
        });
        
        // Set contact data
        if (profileData) {
          setContactData({
            firstName: profileData.first_name || '',
            lastName: profileData.last_name || '',
            email: profileData.email || '',
            phone: profileData.phone || '',
            address: profileData.address || ''
          });
        }
        
        if (data.logo_url) {
          setPreview(data.logo_url);
        }
      } catch (err) {
        console.error('Error fetching producer:', err);
        setError(t('producers.failedToLoad', 'Kunde inte ladda producentinformation'));
      } finally {
        setLoading(false);
      }
    }

    fetchProducer();
  }, [id, user, t]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setUploadError('');

    if (!selectedFile) {
      return;
    }

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
      setUploadError(t('common.invalidFileType', 'Ogiltig filtyp. Tillåtna typer: {{types}}', {
        types: ALLOWED_FILE_TYPES.join(', ')
      }));
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setUploadError(t('common.fileTooLarge', 'Filen är för stor. Maximal storlek: {{size}}MB', {
        size: MAX_FILE_SIZE / 1024 / 1024
      }));
      return;
    }

    setFile(selectedFile);
    const url = URL.createObjectURL(selectedFile);
    setPreview(url);
  };

  const removeImage = () => {
    setFile(null);
    setPreview(null);
  };

  async function uploadLogo() {
    if (!file || !id) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `${id}-${Date.now()}.${fileExt}`;
    const filePath = `producer_logos/${fileName}`;

    try {
      console.log('Uploading file to storage...');
      
      // Upload to product_images bucket which is already configured in Supabase
      const { error: uploadError } = await supabase.storage
        .from('product_images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('File uploaded, generating public URL');
      const { data } = supabase.storage
        .from('product_images')
        .getPublicUrl(filePath);
        
      return data.publicUrl;
    } catch (err) {
      console.error('Error uploading logo:', err);
      throw err;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (!user) {
      setError(t('producers.loginRequired', 'Du måste vara inloggad för att uppdatera en producent'));
      setLoading(false);
      return;
    }

    try {
      if (!id) {
        throw new Error(t('producers.missingId', 'Producent-ID saknas'));
      }
      
      console.log('Updating producer data for ID:', id);
      
      // Parallel promises for each update to make it as fast as possible
      const updatePromises = [];
      
      // 1. Handle logo upload if needed
      let logoUrl = producer?.logo_url;
      if (file) {
        try {
          logoUrl = await uploadLogo();
          console.log('Logo uploaded, URL:', logoUrl);
        } catch (error) {
          console.error('Logo upload failed:', error);
          logoUrl = producer?.logo_url; // Keep existing logo on error
        }
      } else if (!preview) {
        logoUrl = null; // Clear logo if no preview
      }
      
      // 2. Update producer data
      updatePromises.push(
        supabase
          .from('producers')
          .update({
            company_name: formData.companyName,
            description: formData.description,
            logo_url: logoUrl,
            website_url: formData.websiteUrl,
            facebook_url: formData.facebookUrl,
            instagram_url: formData.instagramUrl,
            twitter_url: formData.twitterUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)
      );
      
      // 3. First verify if the profile exists
      const { data: profileData, error: profileFetchError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', id)
        .single();
      
      if (profileFetchError && profileFetchError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileFetchError);
        throw new Error('Failed to verify profile: ' + profileFetchError.message);
      }
      
      console.log('Updating contact info for profile ID:', id);
      
      // 4. Update or create profile with contact info
      if (!profileData) {
        console.warn('No profile found with ID:', id);
        console.log('Attempting to create a new profile for this producer');
        
        // Try to create a new profile
        updatePromises.push(
          supabase
            .from('profiles')
            .insert({
              id: id,  // Use the producer ID as the profile ID
              first_name: contactData.firstName,
              last_name: contactData.lastName,
              email: contactData.email,
              phone: contactData.phone,
              address: contactData.address,
              role: 'producer',  // Set default role
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
        );
      } else {
        // Update existing profile
        updatePromises.push(
          supabase
            .from('profiles')
            .update({
              first_name: contactData.firstName,
              last_name: contactData.lastName,
              email: contactData.email,
              phone: contactData.phone,
              address: contactData.address,
              updated_at: new Date().toISOString()
            })
            .eq('id', id)
        );
      }
      
      // Wait for updates to complete
      const results = await Promise.all(updatePromises);
      
      // Check for errors
      const producerError = results[0].error;
      const profileError = results[1]?.error;  // Handle case where profile update might not be included
      
      if (producerError) {
        console.error('Producer update failed:', producerError);
        throw new Error('Failed to update producer: ' + producerError.message);
      }
      
      if (profileError) {
        console.error('Contact info update failed:', profileError);
        throw new Error('Failed to update contact information: ' + profileError.message);
      }
      
      console.log('Producer update result:', results[0].data);
      console.log('Profile update result:', results[1]?.data);
      console.log('Contact data being saved:', contactData);
      console.log('All updates completed successfully');
      
      // Show success message
      setSuccessMessage(t('producers.updateSuccess', 'Producent uppdaterad'));
      toast.success(t('producers.updateSuccess', 'Producent uppdaterad'));
      
      // Wait briefly for the success message to be seen before redirecting
      setTimeout(() => {
        navigate('/admin/producers');
      }, 1500);
      
    } catch (err) {
      console.error('Error updating producer:', err);
      setError(t('producers.updateFailed', 'Kunde inte uppdatera producent: {{message}}', {
        message: err instanceof Error ? err.message : t('common.unknownError', 'Okänt fel')
      }));
      toast.error(t('common.updateFailed', 'Uppdatering misslyckades'));
    } finally {
      setLoading(false);
    }
  }

  if (loading && !producer) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <p className="text-red-600">{t('common.authRequired', 'Autentisering krävs. Vänligen logga in igen.')}</p>
        <button 
          onClick={() => navigate('/admin')} 
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          {t('common.returnToAdmin', 'Återgå till adminportalen')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-4">
        <button 
          onClick={() => navigate('/admin/producers')} 
          className="text-gray-500 hover:text-gray-700 mr-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('producers.edit', 'Redigera producent')}: {producer?.company_name}
        </h1>
        <div className="ml-auto">
          <button
            onClick={() => navigate(`/admin/producers/${id}/products`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Package className="h-5 w-5 mr-2" />
            {t('producers.manageProducts', 'Hantera produkter')}
          </button>
        </div>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="mb-4 rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">{successMessage}</div>
        </div>
      )}

      {/* Main form with contact info and producer details */}
      <form onSubmit={handleSubmit} data-testid="producer-edit-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contact info in a card */}
          <div className="bg-gray-50 p-4 rounded-md h-fit">
            <h2 className="text-sm font-medium text-gray-700 mb-4">{t('common.contactInfo', 'Kontaktinformation')}</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  {t('common.firstName', 'Förnamn')}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={contactData.firstName}
                  onChange={(e) => setContactData({ ...contactData, firstName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  {t('common.lastName', 'Efternamn')}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={contactData.lastName}
                  onChange={(e) => setContactData({ ...contactData, lastName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('common.email', 'E-post')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={contactData.email}
                  onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  {t('common.phone', 'Telefon')}
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={contactData.phone}
                  onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  {t('common.address', 'Adress')}
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={contactData.address}
                  onChange={(e) => setContactData({ ...contactData, address: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Main form taking up 2/3 of the space */}
          <div className="md:col-span-2">
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                {error && (
                  <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">{t('common.error', 'Fel')}</h3>
                        <div className="mt-2 text-sm text-red-700">
                          {error}
                        </div>
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setError('')}
                            className="text-sm font-medium text-red-600 hover:text-red-500"
                          >
                            {t('common.dismiss', 'Stäng')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left column */}
                  <div>
                    <div className="mb-4">
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                        {t('producers.companyName', 'Företagsnamn')}
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        id="companyName"
                        required
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">{t('producers.logo', 'Logotyp')}</label>
                      <div className="mt-1 flex items-center">
                        {preview ? (
                          <div className="relative">
                            <img
                              src={preview}
                              alt={t('producers.logoPreview', 'Förhandsgranskning')}
                              className="h-24 w-24 object-contain border rounded-md"
                            />
                            <button
                              type="button"
                              onClick={removeImage}
                              className="absolute top-0 right-0 bg-red-600 text-white rounded-full p-1 -mt-2 -mr-2"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <label
                              htmlFor="logo-upload"
                              className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500"
                            >
                              <span className="flex items-center space-x-2">
                                <Upload className="h-5 w-5" />
                                <span>{t('producers.uploadLogo', 'Ladda upp logotyp')}</span>
                              </span>
                              <input
                                id="logo-upload"
                                name="logo-upload"
                                type="file"
                                className="sr-only"
                                onChange={handleFileChange}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      {uploadError && (
                        <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {t('common.fileRequirements', 'PNG, JPG, JPEG eller SVG upp till 5MB')}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700">
                        {t('producers.websiteUrl', 'Webbplats URL')}
                      </label>
                      <input
                        type="text"
                        id="websiteUrl"
                        name="websiteUrl"
                        value={formData.websiteUrl}
                        onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Right column - social media */}
                  <div>
                    <div className="mb-4">
                      <label htmlFor="facebookUrl" className="block text-sm font-medium text-gray-700">
                        {t('producers.facebookUrl', 'Facebook URL')}
                      </label>
                      <input
                        type="text"
                        id="facebookUrl"
                        name="facebookUrl"
                        value={formData.facebookUrl}
                        onChange={(e) => setFormData({ ...formData, facebookUrl: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="instagramUrl" className="block text-sm font-medium text-gray-700">
                        {t('producers.instagramUrl', 'Instagram URL')}
                      </label>
                      <input
                        type="text"
                        id="instagramUrl"
                        name="instagramUrl"
                        value={formData.instagramUrl}
                        onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="twitterUrl" className="block text-sm font-medium text-gray-700">
                        {t('producers.twitterUrl', 'Twitter URL')}
                      </label>
                      <input
                        type="text"
                        id="twitterUrl"
                        name="twitterUrl"
                        value={formData.twitterUrl}
                        onChange={(e) => setFormData({ ...formData, twitterUrl: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Description field - full width */}
                <div className="mt-6">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t('common.description', 'Beskrivning')}
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder={t('producers.descriptionPlaceholder', 'Företagsbeskrivning och produktinformation')}
                    className="mt-1"
                    height={150} /* Reduced height */
                  />
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {loading ? t('common.saving', 'Sparar...') : t('common.saveChanges', 'Spara ändringar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
} 